import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Paper from "@mui/material/Paper";
import Portal from "@mui/material/Portal";
import { alpha } from "@mui/system/colorManipulator";
import ChevronRightIcon from "@untitled-ui/icons-react/build/esm/ChevronRight";

import { RouterLink } from "src/components/router-link";
import CreditCard01Icon from "src/icons/untitled-ui/duocolor/credit-card-01";
import HomeSmileIcon from "src/icons/untitled-ui/duocolor/home-smile";
import LayoutAlt02Icon from "src/icons/untitled-ui/duocolor/layout-alt-02";
import LogOut01Icon from "src/icons/untitled-ui/duocolor/log-out-01";
import Mail04Icon from "src/icons/untitled-ui/duocolor/mail-04";
import XSquareIcon from "src/icons/untitled-ui/duocolor/x-square";
import { paths } from "src/paths";
import { useTranslation } from "react-i18next";

interface Item {
  caption?: string;
  children?: {
    external?: boolean;
    path: string;
    title: string;
  }[];
  external?: boolean;
  icon?: JSX.Element;
  path?: string;
  title: string;
}

interface Section {
  items: Item[];
}

const hp: Section[] = [
  {
    items: [
      {
        title: "Referral via Zorgdomein",
        path: paths.zorgdomeinreferral,
        external: false,
      },
      // {
      //   title: "Our Workmethod",
      //   path: paths.workmethod,
      //   external: false,
      // },
      {
        title: "Introduction session",
        path: paths.introductionsession,
        external: false,
      },
    ],
  },
];

const resources: Section[] = [
  {
    items: [
      {
        title: "Blog",
        path: paths.blog.index,
      },
      {
        title: "Apps",
        path: paths.diabetesmanagementapps,
      },
      {
        title: "Basal Testing Calculator",
        path: paths.basalTestingCalculator,
      },
      {
        title: "Research",
        children: [
          {
            title: "Scientific Publications",
            path: paths.publications,
          },
          {
            title: "Community Research",
            path: paths.communityResearch,
          },
          {
            title: "Loop decision guide",
            path: paths.loopDecisionGuide,
          },
        ],
      },
      {
        title: "Accredited courses",
        children: [
          {
            title: "iAPS",
            path: "https://www.diabetotech.com/i-aps?_gl=1*1072e00*_up*MQ..*_ga*MTMwMzU5NTY0Ny4xNzY3NTEyNjIz*_ga_CQY7RM61QB*czE3Njc1MTI2NDAkbzEkZzAkdDE3Njc1MTI2NDAkajYwJGwwJGgw",
            external: true,
          },
          {
            title: "Loop",
            path: "https://www.diabetotech.com/diy-loop?_gl=1*1072e00*_up*MQ..*_ga*MTMwMzU5NTY0Ny4xNzY3NTEyNjIz*_ga_CQY7RM61QB*czE3Njc1MTI2NDAkbzEkZzAkdDE3Njc1MTI2NDAkajYwJGwwJGgw",
            external: true,
          },
          {
            title: "AndroidAPS",
            path: "https://www.diabetotech.com/android-aps?_gl=1*5rozwu*_up*MQ..*_ga*MTMwMzU5NTY0Ny4xNzY3NTEyNjIz*_ga_CQY7RM61QB*czE3Njc1MTI2NDAkbzEkZzAkdDE3Njc1MTI2NDAkajYwJGwwJGgw",
            external: true,
          },
          {
            title: "Trio",
            path: "https://www.diabetotech.com/trio",
            external: true,
          },
        ],
      },
      {
        title: "About Open-Source Looping",
        path: paths.whatIsopensourcelooping,
      },
    ],
  },
];

const pricing: Section[] = [
  {
    items: [
      {
        title: "Nightscout",
        path: paths.nightscout,
      },
      {
        title: "Coaching",
        path: paths.medicalCoaching,
      },
      {
        title: "Prescriptions",
        children: [
          {
            title: "Lyumjev",
            path: paths.lyumjev,
          },
        ],
      },
    ],
  },
];

const more: Section[] = [
  {
    items: [
      {
        title: "Our mission",
        path: paths.workmethod,
      },
      {
        title: "Our contributions",
        path: paths.contributions,
      },
      {
        title: "Data & Privacy",
        path: paths.dataprivacy,
      },
      {
        title: "Newsletter",
        path: paths.newsletter,
      },
      {
        title: "Contact",
        path: "mailto:info@opensource.clinic",
      },
      // {
      //   title: "Events",
      //   path: paths.events,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <Mail04Icon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   title: "Checkout",
      //   path: paths.checkout,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <LogOut01Icon />
      //     </SvgIcon>
      //   ),
      // },
      // {
      //   title: "Error",
      //   path: paths.notFound,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <XSquareIcon />
      //     </SvgIcon>
      //   ),
      // },
    ],
  },
];

export const HpPopover: FC = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      {hp.map((section, index) => {
        return (
          <Stack
            component="ul"
            key={index}
            spacing={0.5}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {section.items.map((item) => {
              const linkProps = item.path
                ? item.external
                  ? {
                      component: "a",
                      href: item.path,
                      target: "_blank",
                    }
                  : {
                      component: RouterLink,
                      href: item.path,
                    }
                : {};

              return (
                <li key={t(item.title)}>
                  <ButtonBase
                    sx={{
                      alignItems: "center",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "flex-start",
                      px: "12px",
                      py: "6px",
                      textAlign: "left",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    {...linkProps}
                  >
                    {item.icon && (
                      <Box
                        component="span"
                        sx={{
                          alignItems: "center",
                          color: "action.active",
                          display: "inline-flex",
                          justifyContent: "center",
                          mr: 2,
                          width: 10,
                        }}
                      >
                        {item.icon}
                      </Box>
                    )}
                    <Box component="span" sx={{ flexGrow: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          fontFamily: (theme) => theme.typography.fontFamily,
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: "24px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(item.title)}
                      </Box>
                      {item.caption && (
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "18px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.caption}
                        </Box>
                      )}
                    </Box>
                  </ButtonBase>
                </li>
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
};

interface NestedMenuItemProps {
  item: Item;
  t: (key: string) => string;
}

const NestedMenuItem: FC<NestedMenuItemProps> = ({ item, t }) => {
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const handleMouseEnter = useCallback(() => {
    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top,
        left: rect.right + 8, // Position submenu to the right with small gap
      });
    }
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Add a small delay before closing to allow mouse movement to popover
    timeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 150); // 150ms delay to allow mouse movement
  }, []);

  const handlePopoverMouseEnter = useCallback(() => {
    // Clear any pending close timeout when mouse enters popover
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setOpen(true);
  }, []);

  const handlePopoverMouseLeave = useCallback(() => {
    // Close popover when mouse leaves it
    setOpen(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <li onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <ButtonBase
          ref={buttonRef}
          sx={{
            alignItems: "center",
            borderRadius: 1,
            display: "flex",
            justifyContent: "space-between",
            px: "12px",
            py: "6px",
            textAlign: "left",
            width: "100%",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          {item.icon && (
            <Box
              component="span"
              sx={{
                alignItems: "center",
                color: "action.active",
                display: "inline-flex",
                justifyContent: "center",
                mr: 2,
                width: 10,
              }}
            >
              {item.icon}
            </Box>
          )}
          <Box component="span" sx={{ flexGrow: 1 }}>
            <Box
              component="span"
              sx={{
                display: "block",
                fontFamily: (theme) => theme.typography.fontFamily,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "24px",
                whiteSpace: "nowrap",
              }}
            >
              {t(item.title)}
            </Box>
            {item.caption && (
              <Box
                component="span"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                }}
              >
                {item.caption}
              </Box>
            )}
          </Box>
          <SvgIcon
            sx={{
              fontSize: 16,
              ml: 1,
            }}
          >
            <ChevronRightIcon />
          </SvgIcon>
        </ButtonBase>
      </li>
      {open && item.children && (
        <Portal>
          <Box
            ref={popoverRef}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
            sx={{
              position: "fixed",
              top: popoverPosition.top,
              left: popoverPosition.left,
              zIndex: (theme) => theme.zIndex.appBar + 200,
            }}
          >
            <Paper
              elevation={16}
              sx={{
                backgroundColor: (theme) =>
                  alpha(theme.palette.background.paper, 0.9),
                backdropFilter: "blur(6px)",
                p: 1,
                minWidth: 180,
              }}
            >
              <Stack
                component="ul"
                spacing={0.5}
                sx={{
                  listStyle: "none",
                  m: 0,
                  p: 0,
                }}
              >
                {item.children.map((child) => {
                  const childLinkProps = child.path
                    ? child.external
                      ? {
                          component: "a",
                          href: child.path,
                          target: "_blank",
                        }
                      : {
                          component: RouterLink,
                          href: child.path,
                        }
                    : {};

                  return (
                    <li key={child.title}>
                      <ButtonBase
                        sx={{
                          alignItems: "center",
                          borderRadius: 1,
                          display: "flex",
                          justifyContent: "flex-start",
                          px: "12px",
                          py: "6px",
                          textAlign: "left",
                          width: "100%",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        {...childLinkProps}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: "text.primary",
                            display: "block",
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: "24px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {child.title}
                        </Box>
                      </ButtonBase>
                    </li>
                  );
                })}
              </Stack>
            </Paper>
          </Box>
        </Portal>
      )}
    </>
  );
};

export const ResourcesPopover: FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      {resources.map((section, index) => {
        return (
          <Stack
            component="ul"
            key={index}
            spacing={0.5}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {section.items.map((item) => {
              // For items with children (Research and Accredited courses), use nested menu
              if (item.children) {
                return <NestedMenuItem key={t(item.title)} item={item} t={t} />;
              }

              // Regular items without children
              const linkProps = item.path
                ? item.external
                  ? {
                      component: "a",
                      href: item.path,
                      target: "_blank",
                    }
                  : {
                      component: RouterLink,
                      href: item.path,
                    }
                : {};

              return (
                <li key={t(item.title)}>
                  <ButtonBase
                    sx={{
                      alignItems: "center",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "flex-start",
                      px: "12px",
                      py: "6px",
                      textAlign: "left",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    {...linkProps}
                  >
                    {item.icon && (
                      <Box
                        component="span"
                        sx={{
                          alignItems: "center",
                          color: "action.active",
                          display: "inline-flex",
                          justifyContent: "center",
                          mr: 2,
                          width: 10,
                        }}
                      >
                        {item.icon}
                      </Box>
                    )}
                    <Box component="span" sx={{ flexGrow: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          fontFamily: (theme) => theme.typography.fontFamily,
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: "24px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(item.title)}
                      </Box>
                      {item.caption && (
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "18px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.caption}
                        </Box>
                      )}
                    </Box>
                  </ButtonBase>
                </li>
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
};

export const PricingPopover: FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      {pricing.map((section, index) => {
        return (
          <Stack
            component="ul"
            key={index}
            spacing={0.5}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {section.items.map((item) => {
              if (item.children) {
                return (
                  <NestedMenuItem key={t(item.title)} item={item} t={t} />
                );
              }

              const linkProps = item.path
                ? item.external
                  ? {
                      component: "a",
                      href: item.path,
                      target: "_blank",
                    }
                  : {
                      component: RouterLink,
                      href: item.path,
                    }
                : {};

              return (
                <li key={t(item.title)}>
                  <ButtonBase
                    sx={{
                      alignItems: "center",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "flex-start",
                      px: "12px",
                      py: "6px",
                      textAlign: "left",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    {...linkProps}
                  >
                    {item.icon && (
                      <Box
                        component="span"
                        sx={{
                          alignItems: "center",
                          color: "action.active",
                          display: "inline-flex",
                          justifyContent: "center",
                          mr: 2,
                          width: 10,
                        }}
                      >
                        {item.icon}
                      </Box>
                    )}
                    <Box component="span" sx={{ flexGrow: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          fontFamily: (theme) => theme.typography.fontFamily,
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: "24px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(item.title)}
                      </Box>
                      {item.caption && (
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "18px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.caption}
                        </Box>
                      )}
                    </Box>
                  </ButtonBase>
                </li>
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
};

export const MorePopover: FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        // display: "grid",
        // gridTemplateColumns: "repeat(2, 1fr)",
        // gap: 3,
        p: 2,
      }}
    >
      {more.map((section, index) => {
        return (
          <Stack
            component="ul"
            key={index}
            spacing={0.5}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {section.items.map((item) => {
              const linkProps = item.path
                ? item.external
                  ? {
                      component: "a",
                      href: item.path,
                      target: "_blank",
                    }
                  : {
                      component: RouterLink,
                      href: item.path,
                    }
                : {};

              return (
                <li key={t(item.title)}>
                  <ButtonBase
                    sx={{
                      alignItems: "center",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "flex-start",
                      px: "12px",
                      py: "6px",
                      textAlign: "left",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    {...linkProps}
                  >
                    {item.icon && (
                      <Box
                        component="span"
                        sx={{
                          alignItems: "center",
                          color: "action.active",
                          display: "inline-flex",
                          justifyContent: "center",
                          mr: 2,
                          width: 10,
                        }}
                      >
                        {item.icon}
                      </Box>
                    )}
                    <Box component="span" sx={{ flexGrow: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          fontFamily: (theme) => theme.typography.fontFamily,
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: "24px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(item.title)}
                      </Box>
                      {item.caption && (
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "18px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.caption}
                        </Box>
                      )}
                    </Box>
                  </ButtonBase>
                </li>
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
};

const normalSections: Section[] = [
  {
    items: [
      {
        title: "Dashboard",
        path: "/dashboard",
        icon: (
          <SvgIcon fontSize="small">
            <HomeSmileIcon />
          </SvgIcon>
        ),
        caption: "40+ screens",
        children: [
          {
            title: "Overview",
            path: paths.dashboard.index,
          },
          {
            title: "Customers",
            path: paths.dashboard.customers.index,
          },
          {
            title: "Logistics",
            path: paths.dashboard.logistics.index,
          },
          {
            title: "File Manager",
            path: paths.dashboard.fileManager,
          },
          {
            title: "Academy",
            path: paths.dashboard.academy.index,
          },
        ],
      },
    ],
  },
  {
    items: [
      {
        title: "Blog",
        path: paths.blog.index,
        icon: (
          <SvgIcon fontSize="small">
            <LayoutAlt02Icon />
          </SvgIcon>
        ),
      },
      {
        title: "Pricing",
        path: paths.pricing,
        icon: (
          <SvgIcon fontSize="small">
            <CreditCard01Icon />
          </SvgIcon>
        ),
      },
      // {
      //   title: "Events",
      //   path: paths.events,
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <Mail04Icon />
      //     </SvgIcon>
      //   ),
      // },
      {
        title: "Checkout",
        path: paths.checkout,
        icon: (
          <SvgIcon fontSize="small">
            <LogOut01Icon />
          </SvgIcon>
        ),
      },
      {
        title: "Error",
        path: paths.notFound,
        icon: (
          <SvgIcon fontSize="small">
            <XSquareIcon />
          </SvgIcon>
        ),
      },
    ],
  },
];

export const NormalPopover: FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        gridTemplateColumns: "repeat(2, 1fr)",
        p: 3,
      }}
    >
      {normalSections.map((section, index) => {
        return (
          <Stack
            component="ul"
            key={index}
            spacing={0.5}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {section.items.map((item) => {
              const linkProps = item.path
                ? item.external
                  ? {
                      component: "a",
                      href: item.path,
                      target: "_blank",
                    }
                  : {
                      component: RouterLink,
                      href: item.path,
                    }
                : {};

              return (
                <li key={t(item.title)}>
                  <ButtonBase
                    sx={{
                      alignItems: "center",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "flex-start",
                      px: "12px",
                      py: "6px",
                      textAlign: "left",
                      width: "100%",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    {...linkProps}
                  >
                    <Box
                      component="span"
                      sx={{
                        alignItems: "center",
                        color: "action.active",
                        display: "inline-flex",
                        justifyContent: "center",
                        mr: 2,
                        width: 20,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box component="span" sx={{ flexGrow: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          fontFamily: (theme) => theme.typography.fontFamily,
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: "24px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(item.title)}
                      </Box>
                      {item.caption && (
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            fontFamily: (theme) => theme.typography.fontFamily,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "18px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.caption}
                        </Box>
                      )}
                    </Box>
                  </ButtonBase>
                  {item.children && (
                    <Stack
                      component="ul"
                      spacing={0.5}
                      sx={{
                        listStyle: "none",
                        m: 0,
                        p: 0,
                        pl: 20 + 16 + "px", // icon size + icon margin
                      }}
                    >
                      {item.children.map((child) => {
                        const linkProps = child.path
                          ? child.external
                            ? {
                                component: "a",
                                href: child.path,
                                target: "_blank",
                              }
                            : {
                                component: RouterLink,
                                href: child.path,
                              }
                          : {};

                        return (
                          <li key={child.title}>
                            <ButtonBase
                              sx={{
                                alignItems: "center",
                                borderRadius: 1,
                                display: "flex",
                                justifyContent: "flex-start",
                                px: "12px",
                                py: "6px",
                                textAlign: "left",
                                width: "100%",
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                              {...linkProps}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                  fontFamily: (theme) =>
                                    theme.typography.fontFamily,
                                  fontSize: 14,
                                  fontWeight: 500,
                                  lineHeight: "24px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {child.title}
                              </Box>
                            </ButtonBase>
                          </li>
                        );
                      })}
                    </Stack>
                  )}
                </li>
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
};
